import React, { useRef, useEffect } from "react";
import { BUSINESS_NAME } from "@/core/constants/business";
import {
  animateHeading,
  animateDescription,
  animateButtons,
  cleanupScrollTriggers,
} from "./gsap";

interface CTAProps {
  googleMapsUrl: string;
}

const CTA: React.FC<CTAProps> = ({ googleMapsUrl }) => {
  const sectionRef = useRef<HTMLElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const buttonsRef = useRef<HTMLDivElement>(null);
  const scrollTriggerInstances = useRef<ScrollTrigger[]>([]);

  useEffect(() => {
    // Apply animations with a small timeout to ensure DOM is ready
    const animationTimeout = setTimeout(() => {
      const triggers = [];

      // Animate heading
      const headingTrigger = animateHeading(
        { current: headingRef.current },
        { current: sectionRef.current }
      );
      if (headingTrigger) triggers.push(headingTrigger);

      // Animate description
      const descriptionTrigger = animateDescription(
        { current: descriptionRef.current },
        { current: sectionRef.current }
      );
      if (descriptionTrigger) triggers.push(descriptionTrigger);

      // Animate buttons
      const buttonsTrigger = animateButtons(
        { current: buttonsRef.current },
        { current: sectionRef.current }
      );
      if (buttonsTrigger) triggers.push(buttonsTrigger);

      // Store triggers for cleanup
      scrollTriggerInstances.current = triggers;
    }, 100);

    // Cleanup function
    return () => {
      clearTimeout(animationTimeout);
      cleanupScrollTriggers();
    };
  }, []);

  return (
    <section ref={sectionRef} className="py-16 bg-law-black text-white">
      <div className="container mx-auto px-4 text-center">
        <h2 ref={headingRef} className="text-3xl md:text-4xl font-bold mb-6">
          Ready to Visit?
        </h2>
        <p ref={descriptionRef} className="text-xl mb-8 max-w-2xl mx-auto">
          Stop by {BUSINESS_NAME} today to experience our premium cannabis
          products and meet our friendly, knowledgeable staff.
        </p>
        <div
          ref={buttonsRef}
          className="flex flex-col sm:flex-row justify-center gap-4"
        >
          <a href="/products" className="btn-primary">
            View Products
          </a>
          <a
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary"
          >
            Get Directions
          </a>
        </div>
      </div>
    </section>
  );
};

export default CTA;
